export const normalizeBillingType = tierOrType => {
  const value =
    typeof tierOrType === 'string'
      ? tierOrType
      : tierOrType?.billingType || tierOrType?.tierSnapshot?.billingType;
  return value === 'one_time' ? 'one_time' : 'recurring';
};

export const isOneTimeTier = tier => normalizeBillingType(tier) === 'one_time';

// Short "/mo"-style suffix for a price. Mirrors the server's
// billingIntervalService.intervalSuffix -- kept as a separate JS
// implementation (no shared package between client/server here) but must
// stay in sync with it.
export const billingIntervalSuffix = tier => {
  const interval = tier?.billingInterval || tier?.billingCycle;
  if (interval === 'year' || interval === 'yearly') return '/yr';
  if (interval === 'week' || interval === 'weekly') return '/wk';
  if (interval === 'custom') {
    const count = Number(tier?.customIntervalCount) || 1;
    const unit = tier?.customIntervalUnit || 'month';
    return `/ every ${count} ${count === 1 ? unit : `${unit}s`}`;
  }
  return '/mo';
};

// Long form for use in a sentence -- "month", "year", "week", or "2 weeks"
// for a custom cadence (as in "Includes 5 credits per 2 weeks").
export const billingIntervalLabel = tier => {
  const interval = tier?.billingInterval || tier?.billingCycle;
  if (interval === 'year' || interval === 'yearly') return 'year';
  if (interval === 'week' || interval === 'weekly') return 'week';
  if (interval === 'custom') {
    const count = Number(tier?.customIntervalCount) || 1;
    const unit = tier?.customIntervalUnit || 'month';
    return count === 1 ? unit : `${count} ${unit}s`;
  }
  return 'month';
};

export const formatTierPriceSuffix = tier => {
  if (isOneTimeTier(tier)) {
    return '';
  }
  return billingIntervalSuffix(tier);
};

export const formatTierPriceLabel = (priceAmountCents, tier, { currency = 'USD' } = {}) => {
  const cents = Number(priceAmountCents || 0);
  const dollars = (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
  const suffix = formatTierPriceSuffix(tier);
  return `$${dollars}${suffix}`;
};

export const getTierCheckoutActionLabel = tier =>
  isOneTimeTier(tier) ? 'Purchase' : 'Subscribe';

export const getTierCheckoutActionLoadingLabel = tier =>
  isOneTimeTier(tier) ? 'Redirecting...' : 'Redirecting...';

export const formatTierCreditsPeriodSuffix = tier => {
  if (isOneTimeTier(tier)) {
    return '';
  }
  // credits.frequency always mirrors the tier's own billingInterval (see
  // sanitizeCreditsInput server-side) -- customIntervalUnit/Count live on
  // the tier itself, not duplicated onto credits, so they're carried over
  // here rather than read from `credits`.
  const frequency = tier?.credits?.frequency || tier?.frequency || tier?.billingInterval;
  return billingIntervalSuffix({ ...tier, billingInterval: frequency });
};

export const formatTierCreditsSummary = tier => {
  const credits = tier?.credits;
  if (!credits?.enabled) {
    return 'No session credits included';
  }

  const creditCount = Number(credits.creditsPerPeriod || 0);
  if (isOneTimeTier(tier)) {
    if (credits.allSessions !== false) {
      return `Includes ${creditCount} credit${creditCount === 1 ? '' : 's'} with purchase`;
    }
    return `Includes ${creditCount} credit${creditCount === 1 ? '' : 's'} with purchase for selected sessions`;
  }

  const frequency = billingIntervalLabel({
    billingInterval: credits.frequency || tier?.billingInterval,
    customIntervalUnit: tier?.customIntervalUnit,
    customIntervalCount: tier?.customIntervalCount,
  });
  if (credits.allSessions !== false) {
    return `Includes ${creditCount} credits per ${frequency} for all sessions`;
  }
  return `Includes ${creditCount} credits per ${frequency} for selected sessions`;
};
